
import { Component, Input, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderConfig } from 'src/app/components/widgets/loader/loader.config';
import { AlertInfo } from 'src/app/models/alert-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CustomerService } from 'src/app/services/api/customer.service';
import { LoaderService } from 'src/app/services/loader.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';

interface DocumentFile {
  customerDocumentJoinedId: number,
  customerDocument: {
    customerDocumentId: number
    language: {
      languageId: number
      name: string
    }
    type: string
    name: string
    attachments: [{
      attachmentId: number
      fileNameOriginal: string
      url: string
    }]
  },
  customerAttachments: [{
    attachmentId: number
    fileNameOriginal: string
    url: string
  }],
  status: 'WAITING_FOR_CUSTOMER' | 'WAITING_FOR_OPERATOR' | 'REJECTED' | 'ACCEPTED';
}

export interface Root {

}



interface UploadStatus {
  success: boolean;
  message: string;
}

@Component({
  selector: 'documents-panel',
  imports: [ReactiveFormsModule, LoaderComponent, SelectFieldComponent],
  templateUrl: './documents-panel.component.html',
  styleUrl: './documents-panel.component.scss'
})
export class DocumentsPanelComponent implements OnInit, OnDestroy {
  @Input() customer: any;
  @Input() readonlyDocumentsPanel!: boolean;
  @Input() hideAddDocument: boolean = false;

  files: DocumentFile[] = [];
  selectedFile: File | null = null;
  showConfirmation: boolean = false;
  message: string = '';
  fileDeleted: any;
  uploadStatus: UploadStatus | null = null;
  isLoading: boolean = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  selectedDocumentType: string = 'agreement';
  showAddFileDialog: boolean = false;
  @ViewChild('addFileModalContent') private addFileModalContent!: TemplateRef<unknown>;
  private addFileModalRef?: NgbModalRef;
  areAllDocsVerified: boolean = false;
  canViewConfirmedColumn: boolean = false;
  canSendReminder: boolean = false;
  canUploadDocuments: boolean = false;

  get hasFiles(): boolean {
    return Array.isArray(this.files) && this.files.length > 0;
  }

  extraDocuments: SelectOption[] = [];
  selectedExtraDocumentControl = new FormControl('', [Validators.required]);
  selectedExtraDocumentId: number = 0;

  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Loading...',
    overlay: true
  };

  private loaderSubscription: Subscription | null = null;
  private profileSubscription: Subscription | null = null;
  private readonly LOADER_CONTEXT = 'documents-panel';

  constructor(
    private alertNotificationService: AlertNotificationService,
    protected customerService: CustomerService,
    private loaderService: LoaderService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.loaderSubscription = this.loaderService
      .getLoadingAsObservable(this.LOADER_CONTEXT)
      .subscribe(isLoading => this.isLoading = isLoading);

    this.profileSubscription = this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.canViewConfirmedColumn = this.accessControlService.hasFeatureActionAccess(profileCode, 'CUSTOMERS_DOCUMENTS_CONFIRMED_COLUMN', 'view');
      this.canSendReminder = this.accessControlService.hasFeatureActionAccess(profileCode, 'CUSTOMERS_DOCUMENTS_SEND_REMINDER', 'view');
      this.canUploadDocuments = this.accessControlService.hasFeatureActionAccess(profileCode, 'CUSTOMERS_DOCUMENTS_UPLOAD', 'modify');
    });

    setTimeout(() => {
      this.loadAllDocuments();
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.loaderSubscription) {
      this.loaderSubscription.unsubscribe();
    }
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
  }

  loadAllDocuments(): void {
    this.files = [];
    if (this.customer && this.customer.customerId) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);

      this.customerService.listDocumentsForClient(this.customer.customerId).subscribe({
        next: (response) => {
          console.log('Loaded documents:', response);
          if (response.data && response.data.documents && Array.isArray(response.data.documents)) {
            this.files = response.data.documents.filter((doc: DocumentFile) =>
              doc.customerDocument &&
              doc.customerDocument.attachments &&
              doc.customerDocument.attachments.length > 0
            );

            this.checkDocsVerificationStatus();
            this.loaderService.stopLoading(this.LOADER_CONTEXT);
          }
        },
        error: (error) => {
          console.error('Error loading documents:', error);
          this.alertNotificationService.error('Error loading documents');
          this.showAlertInfo('danger', 'Error loading documents');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }

  openAddFileDialog(): void {
    this.showAddFileDialog = true;
    this.loadExtraDocuments();

    this.addFileModalRef = this.modalService.open(this.addFileModalContent, {
      centered: true,
      backdrop: 'static'
    });

    // Chiusura con ESC: equivale a "Cancel".
    this.addFileModalRef.result.catch(() => {
      this.addFileModalRef = undefined;
      if (this.showAddFileDialog) {
        this.closeAddFileDialog();
      }
    });
  }

  loadExtraDocuments(): void {
    if (this.customer && this.customer.customerId) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);
      const options = {
        params: new HttpParams().set('type', 'EXTRA').set('languageId', `${this.customer.language.languageId}`)
      };

      this.customerService.listDocuments(options).subscribe({
        next: (response) => {
          console.log('Extra documents available:', response);
          if (response.data && response.data.customerDocuments && Array.isArray(response.data.customerDocuments)) {
            this.extraDocuments = response.data.customerDocuments.map((doc: any) => {
              return {
                key: doc.customerDocumentId,
                value: doc.name
              };
            });
            this.selectedExtraDocumentControl.setValue('');
          }
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        },
        error: (error) => {
          console.error('Error loading extra documents:', error);
          this.alertNotificationService.error('Error loading available documents');
          this.showAlertInfo('danger', 'Error loading available documents');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }

  closeAddFileDialog(): void {
    this.showAddFileDialog = false;
    this.selectedExtraDocumentControl.setValue('');
    this.selectedExtraDocumentId = 0;

    const ref = this.addFileModalRef;
    this.addFileModalRef = undefined;
    ref?.dismiss();
  }

  onExtraDocumentSelected(documentId: string): void {
    this.selectedExtraDocumentId = Number(documentId);
  }

  setDocumentType(type: string): void {
    this.selectedDocumentType = type;
  }

  addDocument(): void {
    if (this.selectedExtraDocumentId === 0 || !this.customer || !this.customer.customerId) {
      this.alertNotificationService.warning('Please select a document to add');
      return;
    }

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    const data = {};

    this.customerService.addExtraDocuments(
      this.customer.customerId,
      this.selectedExtraDocumentId,
      data
    ).subscribe({
      next: (response) => {
        console.log('Document added successfully:', response);
        this.alertNotificationService.success('Document added successfully');
        this.showAlertInfo('success', 'Document added successfully');
        this.closeAddFileDialog();
        this.loadAllDocuments();
      },
      error: (error) => {
        console.error('Error adding document:', error);
        this.alertNotificationService.error(`Error adding document: ${error.error?.message || 'Unknown error'}`);
        this.showAlertInfo('danger', `Error adding document: ${error.error?.message || 'Unknown error'}`);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      this.uploadFile(selectedFile, this.selectedDocumentType);
      input.value = '';
      this.closeAddFileDialog();
    }
  }

  downloadFile(fileUrl: string): void {

    if (fileUrl !== '') {
      this.customerService.GET(fileUrl).subscribe({
        next: (response) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        }
      })
    } else {
      this.alertNotificationService.warning('File URL not available');
    }
  }

  sendFile(file: DocumentFile): void {
    if (file.status === 'WAITING_FOR_CUSTOMER' || file.status === 'REJECTED') {
      const input = document.createElement('input');
      input.type = 'file';
      input.onchange = (event: any) => {
        if (event.target.files && event.target.files.length > 0) {
          const selectedFile = event.target.files[0];

          this.loaderService.startLoading(this.LOADER_CONTEXT);
          const formData = this.createFormDataForUpload(this.customer.customerId, selectedFile);

          this.customerService.sendDocuments(this.customer.customerId, file.customerDocument.customerDocumentId, formData).subscribe({
            next: (response) => {
              if (response.status.code === 200) {
                file.status = 'WAITING_FOR_CUSTOMER';
                this.alertNotificationService.success(`File "${selectedFile.name}" successfully sent to client`);
                this.showAlertInfo('success', `File "${selectedFile.name}" successfully sent to client`);
                this.checkDocsVerificationStatus();
                this.loadAllDocuments();
              }
            },
            error: (error) => {
              console.error('Error sending file:', error);
              this.alertNotificationService.error(`Error sending file: ${error.error?.message || 'Unknown error'}`);
              this.showAlertInfo('danger', `Error sending file: ${error.error?.message || 'Unknown error'}`);
            },
            complete: () => {
              this.loaderService.stopLoading(this.LOADER_CONTEXT);
            }
          });
        }
      };
      input.click();
    } else {
      this.downloadFile(file.customerDocument.attachments[0].url);
    }
  }

  toggleFileConfirmation(file: DocumentFile): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (event: any) => {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = event.target.files[0];

        this.loaderService.startLoading(this.LOADER_CONTEXT);
        const formData = this.createFormDataForUpload(this.customer.customerId, selectedFile);

        this.customerService.sendDocuments(this.customer.customerId, file.customerDocument.customerDocumentId, formData).subscribe({
          next: (response) => {
            file.status = 'WAITING_FOR_CUSTOMER';

            if (response.data && response.data.attachment) {
              if (!file.customerAttachments) {
                file.customerAttachments = [{ attachmentId: 0, fileNameOriginal: '', url: '' }];
              }
              if (file.customerAttachments.length <= 0) {
                file.customerDocument.attachments.push({
                  attachmentId: response.data.attachment.attachmentId,
                  fileNameOriginal: selectedFile.name,
                  url: response.data.attachment.url
                });
              } else {
                file.customerDocument.attachments[0] = {
                  attachmentId: response.data.attachment.attachmentId,
                  fileNameOriginal: selectedFile.name,
                  url: response.data.attachment.url
                };
              }
            }

            this.alertNotificationService.success(`File "${selectedFile.name}" successfully sent to client`);
            this.showAlertInfo('success', `File "${selectedFile.name}" successfully sent to client`);
            this.checkDocsVerificationStatus();
          },
          error: (error) => {
            console.error('Error sending file:', error);
            this.alertNotificationService.error(`Error sending file: ${error.error?.message || 'Unknown error'}`);
            this.showAlertInfo('danger', `Error sending file: ${error.error?.message || 'Unknown error'}`);
          },
          complete: () => {
            this.loaderService.stopLoading(this.LOADER_CONTEXT);
          }
        });
      }
    };
    input.click();
  }


  updateFileStatus(file: DocumentFile, newStatus: 'WAITING_FOR_CUSTOMER' | 'WAITING_FOR_OPERATOR' | 'REJECTED' | 'ACCEPTED' = 'WAITING_FOR_CUSTOMER'): void {

    let apiStatus: string;
    let actionMessage: string;

    switch (newStatus) {
      case 'ACCEPTED':
        apiStatus = 'ACCEPTED';
        actionMessage = 'Document accepted';
        break;
      case 'REJECTED':
        apiStatus = 'REJECTED';
        actionMessage = 'Document rejected';
        break;
      default:
        apiStatus = 'REJECTED';
        actionMessage = 'Document rejected';
    }

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.customerService.statusDocuments(
      this.customer.customerId,
      file.customerDocumentJoinedId,
      apiStatus,
      { status: newStatus }
    ).subscribe({
      next: (response) => {
        if (response.status.code === 200) {
          file.status = newStatus;
          this.alertNotificationService.success(actionMessage);
          this.showAlertInfo('success', actionMessage);
          this.checkDocsVerificationStatus();
        }
      },
      error: (error) => {
        console.error('Error updating file status:', error);
        this.alertNotificationService.error(`Error updating file status: ${error.error?.message || 'Unknown error'}`);
        this.showAlertInfo('danger', `Error updating file status: ${error.error?.message || 'Unknown error'}`);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  uploadFile(file: File, documentType: string): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);
    const formData = this.createFormDataForUpload(this.customer.customerId, file);

    const uploadService = documentType === 'agreement'
      ? this.customerService.createOtherAgreements(this.customer.customerId, formData)
      : this.customerService.createOtherDocuments(this.customer.customerId, formData);

    uploadService.subscribe({
      next: (response) => {
        this.alertNotificationService.success(`File "${file.name}" successfully uploaded`);
        this.showAlertInfo('success', `File "${file.name}" successfully uploaded`);
        this.loadAllDocuments();
      },
      error: (error) => {
        console.error('Error uploading file:', error);
        this.alertNotificationService.error(`Error uploading file: ${error.error?.message || 'Unknown error'}`);
        this.showAlertInfo('danger', `Error uploading file: ${error.error?.message || 'Unknown error'}`);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  sendReminder(): void {
    if (!this.customer?.customerId) {
      this.alertNotificationService.warning('Customer not found');
      return;
    }

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.customerService.sendReminderToClient(this.customer.customerId, {}).subscribe({
      next: () => {
        this.alertNotificationService.success('Reminder sent successfully');
        this.showAlertInfo('success', 'Reminder sent successfully');
      },
      error: (error) => {
        console.error('Error sending reminder:', error);
        this.alertNotificationService.error(`Error sending reminder: ${error.error?.message || 'Unknown error'}`);
        this.showAlertInfo('danger', `Error sending reminder: ${error.error?.message || 'Unknown error'}`);
      },
      complete: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  createFormDataForUpload(customerId: any, file: File): FormData {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('data', JSON.stringify(customerId));
    return formData;
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = {
      show: true,
      type,
      message
    };

    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  checkDocsVerificationStatus(): void {
    if (!this.files || this.files.length === 0) {
      this.areAllDocsVerified = true;
      return;
    }

    this.areAllDocsVerified = this.files.every(file => file.status === 'ACCEPTED');

    console.log('Document verification status:', {
      files: this.files.length,
      allVerified: this.areAllDocsVerified,
      statuses: this.files.map(file => file.status)
    });
  }
}
